import moment from 'moment';

import { TranslateService } from '@ngx-translate/core';

import {
  NgbActiveModal,
  NgbDatepickerNavigateEvent
} from '@ng-bootstrap/ng-bootstrap';

import {
  Component,
  Input,
  OnInit
} from '@angular/core';

import { BookingDiaryService }            from './booking-diary.service';
import { BookingDiaryEventModel }         from './typings/booking-diary.event';
import { BookingDiaryMechanicModel }      from './typings/booking-diary.mechanic';
import { BookingDiaryMonthTotalsModel }   from './typings/booking-diary.month';
import { BookingDiaryUnassignedJobModel } from './typings/booking-diary.backlog';
import { DropdownOptionModel }            from '../shared/components/dropdown/typings/dropdown-option';
import { JobStatusBookingPipe }           from '../transaction-center/jobs/transaction-center.jobs.status-booking.pipe';
import { MessagesService }                from '../messages/messages.service';
import { MessengerService }               from '../messages/messenger/messenger.service';

import {
  BookingDiaryCustomerModel,
  BookingDiaryProductModel,
  BookingDiaryVehicleModel
} from './typings/booking-diary.customer';

import {
  DATE_FORMAT,
  formatClockLabel,
  MIN_HOURS,
  SLOT_MINUTES
} from './booking-diary.layout';

import {
  mechanicOptionsFor,
  timeOptionsBetween,
  vehicleLabel,
  vehicleOptionsFor
} from './booking-diary.options';

import {
  statusCodeFor,
  statusToneClass
} from '../transaction-center/jobs/transaction-center.jobs.status-tone';

import {
  busyRunsFor,
  freeRunsBetween,
  startsThatFit
} from './booking-diary.availability';

// far enough to cover a fortnight's work without walking the calendar forever when a shop is full
const MAX_SEARCH_DAYS : number = 14;

// one row's place in the openings it has found; days are read only as the user steps past them
interface BookingDiarySlotCursor {
  found              : Array<BookingDiarySlotProposal>;
  fromDate           : string;
  isHoldingMechanic  : boolean;
  index              : number;
  isExhausted        : boolean;
  isSearching        : boolean;
  scannedDays        : number;
}

interface BookingDiarySlotProposal {
  date         : string;
  mechanicId   : string;
  mechanicName : string;
  startMinutes : number;
}

const SEARCH_MAX_ROWS  : number = 8;
const SEARCH_MIN_CHARS : number = 2;

// One appointment: who, what and when, changeable in place, with the ways to message, open, remove
@Component({
  selector   : 'wor-booking-diary-appointment-modal',
  template: require('./booking-diary.appointment-modal.component.html')
})
export class BookingDiaryAppointmentModalComponent implements OnInit {
  // supplied by the diary: closes the card and runs its remove flow, confirmation and all
  @Input() deleteAppointment : () => void;
  @Input() event             : BookingDiaryEventModel;
  @Input() mechanics         : Array<BookingDiaryMechanicModel> = [];

  // supplied by the diary: closes the card and opens the full booking
  @Input() openBooking       : () => void;
  @Input() shopClosesMinutes : number;
  @Input() shopOpensMinutes  : number;

  // set when the card stands for a backlog job, which has no appointment on the grid yet
  @Input() unassignedJob     : BookingDiaryUnassignedJobModel   = null;

  anySlots              : BookingDiarySlotCursor          = null;
  customer              : BookingDiaryCustomerModel       = null;
  date                  : string                          = '';
  error                 : string                          = null;
  hasPickedVehicle      : boolean                         = false;
  hours                 : number                          = 0;
  isChangingItem        : boolean                         = false;
  isChangingMechanic    : boolean                         = false;
  isChangingSchedule    : boolean                         = false;
  isChangingVehicle     : boolean                         = false;
  isLoading             : boolean                         = true;
  isLoadingVehicles     : boolean                         = false;
  isSaving              : boolean                         = false;
  isShowingAvailability : boolean                         = false;
  item                  : any                             = null;
  mechanicId            : string                          = null;
  mechanicOptions       : Array<DropdownOptionModel>      = [];
  mineSlots             : BookingDiarySlotCursor          = null;
  minHours              : number                          = MIN_HOURS;
  product               : BookingDiaryProductModel        = null;
  productQuery          : string                          = '';
  startMinutes          : number                          = 0;
  startOptions          : Array<DropdownOptionModel>      = [];
  timeOptions           : Array<DropdownOptionModel>      = [];
  vehicleId             : string                          = null;
  vehicleOptions        : Array<DropdownOptionModel>      = [];
  vehicles              : Array<BookingDiaryVehicleModel> = [];

  // a day that cannot hold the length greys; hours are coarse, so the times and the save decide
  isDayDisabled = ( date : { year : number; month : number; day : number } ) : boolean => {
    if ( !this.mechanicId || this.durationMinutes <= 0 ) {
      return false;
    }

    const dateStr = `${ date.year }-${ String(date.month).padStart(2, '0') }-${ String(date.day).padStart(2, '0') }`;
    const totals  = this.monthAvailability[dateStr.slice(0, 7)];

    if ( !totals ) {
      return false;
    }

    // a day the roster does not mention has nobody working it
    const day = totals[dateStr];

    if ( !day ) {
      return true;
    }

    const mechanic = day.mechanics.find(known => String(known.id) === String(this.mechanicId));

    if ( !mechanic ) {
      return true;
    }

    // the booking's own hours still occupy its slot, so they are handed back for its own day
    const ownHours = dateStr === this.event.date && String(this.mechanicId) === String(this.event.mechanicId)
      ? ( this.event.endMinutes - this.event.startMinutes ) / 60
      : 0;

    return mechanic.availableHours + ownHours < this.durationMinutes / 60;
  };

  searchProducts = ( typed : string ) : Promise<Array<BookingDiaryProductModel>> => {
    const query = ( typed || '' ).trim();

    if ( query.length < SEARCH_MIN_CHARS ) {
      return Promise.resolve([]);
    }

    return this.bookingDiaryService.searchLabourProducts(query)
      .then(products => products.slice(0, SEARCH_MAX_ROWS))
      .catch(() => []);
  }

  // each day is read once and shared by the rows and the save check
  private dayCache          : { [date : string] : Array<BookingDiaryEventModel> }                       = {};
  private monthAvailability : { [month : string] : { [date : string] : BookingDiaryMonthTotalsModel } } = {};
  private monthsRequested   : { [month : string] : boolean }                                            = {};
  private startGeneration   : number                                                                    = 0;

  constructor (
    public activeModal           : NgbActiveModal,
    private bookingDiaryService  : BookingDiaryService,
    private jobStatusBookingPipe : JobStatusBookingPipe,
    private messagesService      : MessagesService,
    private messengerService     : MessengerService,
    private translateService     : TranslateService
  ) {}

  ngOnInit () : void {
    this.date         = this.event.date;
    this.hours        = ( this.event.endMinutes - this.event.startMinutes ) / 60;
    this.mechanicId   = this.event.mechanicId || null;
    this.startMinutes = this.event.startMinutes;
    this.vehicleId    = this.event.vehicleId || null;

    this.mechanicOptions = mechanicOptionsFor(this.mechanics, this.translateService);
    this.timeOptions     = timeOptionsBetween(this.shopOpensMinutes, this.shopClosesMinutes);

    this.buildVehicleOptions();
    this.refreshStartOptions();
    this.loadMonthAvailability(this.date);
    this.loadItem();
    this.loadCustomer();
    this.loadVehicles();
  }

  cancelItemChange () : void {
    this.isChangingItem = false;
    this.productQuery   = '';
  }

  get canSave () : boolean {
    return !this.isLoading
      && !this.isSaving
      && !!this.date
      && this.startMinutes != null
      && this.durationMinutes > 0;
  }

  // a row can always step back to what it has shown, and on until the fortnight is out
  canStepBack ( cursor : BookingDiarySlotCursor ) : boolean {
    return !!cursor && cursor.index > 0;
  }

  canStepOn ( cursor : BookingDiarySlotCursor ) : boolean {
    return !!cursor
      && !cursor.isSearching
      && ( cursor.index + 1 < cursor.found.length || !cursor.isExhausted );
  }

  clearError () : void {
    this.error = null;
  }

  // the number to ring; the address sits behind the email button, so the row does not repeat it
  get contactLabel () : string {
    return this.customer
      ? this.customer.mobile || this.customer.phone || ''
      : '';
  }

  get dateLabel () : string {
    return moment(this.date, DATE_FORMAT).format('ddd D MMM YYYY');
  }

  get durationMinutes () : number {
    return Math.round(( this.hours || 0 ) * 60);
  }

  get endMinutes () : number {
    return this.startMinutes + this.durationMinutes;
  }

  goToBooking () : void {
    if ( this.openBooking ) {
      this.openBooking();
    }
  }

  get hasDurationChange () : boolean {
    return this.durationMinutes !== this.event.endMinutes - this.event.startMinutes;
  }

  get hasProductChange () : boolean {
    return !!this.product && ( !this.item || this.product.id !== this.item.product_id );
  }

  get hasScheduleChange () : boolean {
    return this.date !== this.event.date
      || this.startMinutes !== this.event.startMinutes
      || ( this.mechanicId || null ) !== ( this.event.mechanicId || null );
  }

  // only a vehicle the user picked is written, so a card opened without one cannot clear it
  get hasVehicleChange () : boolean {
    return this.hasPickedVehicle && ( this.vehicleId || null ) !== ( this.event.vehicleId || null );
  }

  // a booking with no vehicle on it has nothing to link to, so the list is offered straight away
  get hasVehicleLink () : boolean {
    return !this.isChangingVehicle && !!this.vehicleId;
  }

  // a backlog job is not on the grid, so it only needs a free slot once a mechanic is picked
  get isTakingSlot () : boolean {
    return !this.unassignedJob || !!this.mechanicId;
  }

  get itemLabel () : string {
    const source = this.product || this.item;

    return source
      ? [ source.item_code, source.description ].filter(part => !!part).join(' · ')
      : '';
  }

  get itemRate () : number {
    const rate = this.product
      ? this.product.retail_price
      : this.item && this.item.unit_price;

    return rate == null || rate === ''
      ? null
      : Number(rate);
  }

  get mechanicName () : string {
    const mechanic = this.mechanics.find(known => String(known.id) === String(this.mechanicId));

    return mechanic
      ? mechanic.name
      : '';
  }

  // the day, the length and the mechanic all change what is free, so the openings start again
  onAvailabilityChanged () : void {
    this.clearError();
    this.refreshStartOptions();

    if ( this.isShowingAvailability ) {
      this.openSlotRows();
    }
  }

  onCalendarNavigate ( navigation : NgbDatepickerNavigateEvent ) : void {
    if ( navigation && navigation.next ) {
      this.loadMonthAvailability(`${ navigation.next.year }-${ String(navigation.next.month).padStart(2, '0') }-01`);
    }
  }

  // picking one settles the field back to the name
  onMechanicChange ( value : string | number ) : void {
    this.mechanicId = value === null || value === undefined
      ? null
      : String(value);

    this.isChangingMechanic = false;

    this.onAvailabilityChanged();
  }

  onStartChange ( value : string | number ) : void {
    this.startMinutes = Number(value);

    this.clearError();
  }

  // picking one settles the field back to the link; clearing it leaves the list up, with no link
  onVehicleChange ( value : string | number ) : void {
    this.vehicleId = value === null || value === undefined
      ? null
      : String(value);

    this.hasPickedVehicle  = true;
    this.isChangingVehicle = !this.vehicleId;

    this.clearError();
  }

  productLabel ( product : BookingDiaryProductModel ) : string {
    return [ product.item_code, product.description ].filter(part => !!part).join(' · ');
  }

  proposalLabel ( proposal : BookingDiarySlotProposal ) : string {
    const when = `${ moment(proposal.date, DATE_FORMAT).format('ddd D MMM') } · ${ formatClockLabel(proposal.startMinutes) }`;

    return proposal.mechanicName
      ? `${ when } · ${ proposal.mechanicName }`
      : when;
  }

  remove () : void {
    if ( this.deleteAppointment ) {
      this.deleteAppointment();
    }
  }

  // the writes happen here, so a refusal lands in the card with the form intact
  save () : void {
    this.clearError();

    if ( !this.canSave ) {
      return;
    }

    if ( this.durationMinutes < SLOT_MINUTES ) {
      this.error = this.translateService.instant('BOOKING_SPACE.DIARY.APPOINTMENT_MIN_LENGTH', { minutes: SLOT_MINUTES });

      return;
    }

    if ( this.endMinutes > this.shopClosesMinutes ) {
      this.error = this.translateService.instant('BOOKING_SPACE.DIARY.APPOINTMENT_PAST_CLOSING');

      return;
    }

    // a product is written onto the labour line, so a booking without one cannot take it
    if ( this.hasProductChange && !this.item ) {
      this.error = this.translateService.instant('BOOKING_SPACE.DIARY.NO_LABOUR_LINE');

      return;
    }

    const product   = this.hasProductChange
      ? this.product
      : null;
    const vehicleId = this.vehicleId || null;

    this.isSaving = true;

    // the day is read before the slot is claimed, since the card may be saving into another day
    this.eventsFor(this.date)
      .then(events => {
        if ( this.isTakingSlot && this.isSlotTaken(events) ) {
          return Promise.reject(this.translateService.instant('BOOKING_SPACE.DIARY.SLOT_TAKEN'));
        }

        return product
          ? this.bookingDiaryService.updateAppointmentProduct(this.event.eventId, this.item, product)
          : null;
      })
      .then(() => this.hasVehicleChange
        ? this.bookingDiaryService.updateEventVehicle(this.event.eventId, vehicleId)
        : null)
      .then(() => this.saveSchedule())
      .then(() => {
        this.activeModal.close();
      })
      .catch(err => {
        this.error = typeof err === 'string'
          ? err
          : ( err && err.message ) || this.translateService.instant('BOOKING_SPACE.DIARY.UPDATE_FAILED');

        this.isSaving = false;
      });
  }

  get scheduleLabel () : string {
    return `${ this.dateLabel } · ${ this.timeLabel } · ${ this.hours }h`;
  }

  get searchDays () : number {
    return MAX_SEARCH_DAYS;
  }

  // the vehicle list loads after the card opens, so until it arrives the booking's words stand in
  get selectedVehicleLabel () : string {
    const vehicle = this.vehicles.find(known => String(known.id) === String(this.vehicleId));

    return vehicle
      ? vehicleLabel(vehicle)
      : this.event.vehicleDescription || '';
  }

  // picking one settles the field back to the name
  selectProduct ( product : BookingDiaryProductModel ) : void {
    this.product = product;

    this.cancelItemChange();
    this.clearError();
  }

  sendEmail () : void {
    this.messengerService.email({
      customer_id  : this.customer.id,
      email_address: this.customer.email,
      name         : this.customer.display_name
    })
      .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT')))
      .catch(() => null);
  }

  sendSms () : void {
    this.messengerService.sms({
      customer_id: this.customer.id,
      name       : this.customer.display_name,
      send_to    : this.customer.mobile
    })
      .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT')))
      .catch(() => null);
  }

  slotOf ( cursor : BookingDiarySlotCursor ) : BookingDiarySlotProposal {
    return cursor
      ? cursor.found[cursor.index] || null
      : null;
  }

  get splitLabel () : string {
    return this.event.totalSplits > 1
      ? this.translateService.instant('BOOKING_SPACE.DIARY.APPOINTMENT_OF', {
        ordering: this.event.ordering || 1,
        total   : this.event.totalSplits
      })
      : '';
  }

  startItemChange () : void {
    this.isChangingItem = true;
  }

  startMechanicChange () : void {
    this.isChangingMechanic = true;
  }

  startScheduleChange () : void {
    this.isChangingSchedule = true;
  }

  startVehicleChange () : void {
    this.isChangingVehicle = true;
  }

  // a status the app does not know has no wording, and then no tag either
  get statusLabel () : string {
    return this.jobStatusBookingPipe.transform(statusCodeFor(this.event.jobStatus)) || '';
  }

  get statusToneClass () : string {
    return statusToneClass(this.event.jobStatus);
  }

  // stepping on past what is known reads the next day, and the one after, until something fits
  step ( cursor : BookingDiarySlotCursor, direction : number ) : void {
    if ( !cursor ) {
      return;
    }

    this.clearError();

    if ( direction < 0 ) {
      cursor.index = Math.max(cursor.index - 1, 0);

      return;
    }

    if ( cursor.index + 1 < cursor.found.length ) {
      cursor.index += 1;

      return;
    }

    this.scanForSlots(cursor);
  }

  get timeLabel () : string {
    return `${ formatClockLabel(this.startMinutes) } – ${ formatClockLabel(this.endMinutes) }`;
  }

  // nothing is looked up until asked; the openings then stand in for the day and time fields
  toggleAvailability () : void {
    this.isShowingAvailability = !this.isShowingAvailability;

    this.clearError();

    if ( this.isShowingAvailability ) {
      this.openSlotRows();
    }
  }

  // ticking one closes the openings and hands back the day, time and mechanic where it landed
  useSlot ( cursor : BookingDiarySlotCursor ) : void {
    const proposal = this.slotOf(cursor);

    if ( !proposal ) {
      return;
    }

    this.clearError();

    this.date                  = proposal.date;
    this.isChangingSchedule    = true;
    this.mechanicId            = proposal.mechanicId;
    this.startMinutes          = proposal.startMinutes;
    this.isShowingAvailability = false;

    this.refreshStartOptions();
  }

  // the empty choice names the state it stands for: still loading, none chosen, or none on file
  private buildVehicleOptions () : void {
    if ( this.isLoadingVehicles ) {
      this.vehicleOptions = [ {
        label: this.translateService.instant('BOOKING_SPACE.DIARY.LOADING_VEHICLES'),
        value: null
      } ];

      return;
    }

    this.vehicleOptions = vehicleOptionsFor(this.vehicles, this.translateService);
  }

  private eventsFor ( date : string ) : Promise<Array<BookingDiaryEventModel>> {
    if ( this.dayCache[date] ) {
      return Promise.resolve(this.dayCache[date]);
    }

    return this.bookingDiaryService.getDayEvents(date, this.mechanics)
      .then(events => {
        this.dayCache[date] = events;

        return events;
      });
  }

  // false only when the loaded roster for that month leaves the mechanic out of the day
  private isRosteredOn ( date : string, mechanicId : string ) : boolean {
    const totals = this.monthAvailability[date.slice(0, 7)];

    if ( !totals ) {
      return true;
    }

    const day = totals[date];

    return !!day && day.mechanics.some(known => String(known.id) === String(mechanicId));
  }

  // checked against the day the card is saving into, not the period the diary has on screen
  private isSlotTaken ( events : Array<BookingDiaryEventModel> ) : boolean {
    if ( !this.mechanicId ) {
      return false;
    }

    return busyRunsFor(events, this.date, this.mechanicId, this.event.id)
      .some(run => this.startMinutes < run.endMinutes && this.endMinutes > run.startMinutes);
  }

  // the booking carries the customer's name but not the contact details the messaging needs
  private loadCustomer () : void {
    if ( !this.event.customerId ) {
      return;
    }

    this.bookingDiaryService.getCustomer(this.event.customerId)
      .then(customer => {
        this.customer = customer;
      })
      .catch(() => null);
  }

  // the booking knows its labour line's id but not the product on it, so the booking is fetched
  private loadItem () : void {
    this.bookingDiaryService.getEvent(this.event.eventId)
      .then(booking => {
        const items = ( booking && booking.event_items_attributes ) || [];

        this.item = items.find(( entry : any ) => String(entry.id) === String(this.event.eventItemId)) || null;
      })
      .catch(err => {
        this.error = ( err && err.message ) || this.translateService.instant('BOOKING_SPACE.DIARY.LOAD_BOOKING_FAILED');
      })
      .then(() => {
        this.isLoading = false;
      });
  }

  private loadMonthAvailability ( date : string ) : void {
    const month = date.slice(0, 7);

    if ( this.monthsRequested[month] ) {
      return;
    }

    this.monthsRequested[month] = true;

    this.bookingDiaryService.getMonthTotals(date, this.mechanics)
      .then(totals => {
        this.monthAvailability[month] = totals;
      })
      .catch(() => {
        // without the month's answer no day greys; the start times and the save still decide
        this.monthsRequested[month] = false;
      });
  }

  // the picker offers the customer's other vehicles, so a booking put on the wrong one can move
  private loadVehicles () : void {
    if ( !this.event.customerId ) {
      return;
    }

    this.isLoadingVehicles = true;

    this.bookingDiaryService.getCustomerVehicles(this.event.customerId)
      .then(vehicles => {
        this.vehicles = vehicles || [];
      })
      .catch(() => {
        this.vehicles = [];

        this.error = this.translateService.instant('BOOKING_SPACE.DIARY.LOAD_VEHICLES_FAILED');
      })
      .then(() => {
        this.isLoadingVehicles = false;

        this.buildVehicleOptions();
      });
  }

  private mechanicsFor ( cursor : BookingDiarySlotCursor ) : Array<BookingDiaryMechanicModel> {
    return cursor.isHoldingMechanic
      ? this.mechanics.filter(mechanic => String(mechanic.id) === String(this.mechanicId))
      : this.mechanics;
  }

  private newSlotCursor ( isHoldingMechanic : boolean ) : BookingDiarySlotCursor {
    return {
      isHoldingMechanic,
      found      : [],
      fromDate   : this.date,
      index      : 0,
      isExhausted: false,
      isSearching: false,
      scannedDays: 0
    };
  }

  // both rows start again from the current choice, and each finds its own first opening
  private openSlotRows () : void {
    this.anySlots = this.newSlotCursor(false);

    this.mineSlots = this.mechanicId
      ? this.newSlotCursor(true)
      : null;

    this.scanForSlots(this.anySlots);

    if ( this.mineSlots ) {
      this.scanForSlots(this.mineSlots);
    }
  }

  // every opening on a day, earliest first, leaving out where the booking already sits
  private proposalsFor (
    events    : Array<BookingDiaryEventModel>,
    date      : string,
    mechanics : Array<BookingDiaryMechanicModel>
  ) : Array<BookingDiarySlotProposal> {
    const proposals : Array<BookingDiarySlotProposal> = [];

    mechanics.filter(mechanic => this.isRosteredOn(date, mechanic.id)).forEach(mechanic => {
      const free = freeRunsBetween(
        busyRunsFor(events, date, mechanic.id, this.event.id),
        this.shopOpensMinutes,
        this.shopClosesMinutes
      );

      startsThatFit(free, this.durationMinutes).forEach(startMinutes => {
        const isWhereItSits = date === this.date
          && String(mechanic.id) === String(this.mechanicId)
          && startMinutes === this.startMinutes;

        if ( isWhereItSits ) {
          return;
        }

        proposals.push({
          date,
          startMinutes,
          mechanicId  : mechanic.id,
          mechanicName: mechanics.length > 1
            ? mechanic.name
            : null
        });
      });
    });

    return proposals.sort(( a, b ) => a.startMinutes - b.startMinutes);
  }

  // start times the length does not fit go grey, not away; with no mechanic every time stays live
  private refreshStartOptions () : void {
    const generation = ++this.startGeneration;

    if ( !this.mechanicId || this.durationMinutes <= 0 ) {
      this.startOptions = this.timeOptions.map(option => ( { ...option } ));

      return;
    }

    this.eventsFor(this.date)
      .then(events => {
        if ( generation !== this.startGeneration ) {
          return;
        }

        const free = freeRunsBetween(
          busyRunsFor(events, this.date, this.mechanicId, this.event.id),
          this.shopOpensMinutes,
          this.shopClosesMinutes
        );

        const fits = startsThatFit(free, this.durationMinutes);

        this.startOptions = this.timeOptions.map(option => ( {
          ...option,
          isDisabled: fits.indexOf(option.value as number) === -1
        } ));
      })
      .catch(() => {
        if ( generation === this.startGeneration ) {
          this.startOptions = this.timeOptions.map(option => ( { ...option } ));
        }
      });
  }

  // a length change is written through the hours, which carry the day, time and mechanic with them
  private saveSchedule () : Promise<any> {
    if ( this.unassignedJob ) {
      // a mechanic is what puts the job on the diary; without one only the hours are saved
      if ( this.mechanicId ) {
        return this.bookingDiaryService.bookUnassignedJob({
          ...this.unassignedJob,
          estimated_hours: this.durationMinutes / 60
        }, this.date, this.startMinutes, this.mechanicId);
      }

      return this.hasDurationChange
        ? this.bookingDiaryService.updateAppointmentHours(this.unassignedJob.id, this.durationMinutes / 60)
        : Promise.resolve(null);
    }

    if ( this.hasDurationChange ) {
      return this.bookingDiaryService.resizeBooking({
        ...this.event,
        mechanicId  : this.mechanicId || null,
        startMinutes: this.startMinutes
      }, this.date, this.endMinutes);
    }

    return this.hasScheduleChange
      ? this.bookingDiaryService.rescheduleEvent(this.event, this.date, this.startMinutes, this.mechanicId || null)
      : Promise.resolve(null);
  }

  private scanForSlots ( cursor : BookingDiarySlotCursor ) : void {
    const mechanics = this.mechanicsFor(cursor);

    if ( !mechanics.length || this.durationMinutes <= 0 || cursor.scannedDays > MAX_SEARCH_DAYS ) {
      cursor.isExhausted = true;

      return;
    }

    const date = moment(cursor.fromDate, DATE_FORMAT).add(cursor.scannedDays, 'day').format(DATE_FORMAT);

    cursor.isSearching = true;

    this.eventsFor(date)
      .then(events => {
        cursor.isSearching  = false;
        cursor.scannedDays += 1;

        const known = cursor.found.length;

        cursor.found = cursor.found.concat(this.proposalsFor(events, date, mechanics));

        if ( cursor.found.length > known ) {
          cursor.index = known;

          return;
        }

        this.scanForSlots(cursor);
      })
      .catch(() => {
        cursor.isSearching = false;
        cursor.isExhausted = true;

        this.error = this.translateService.instant('BOOKING_SPACE.DIARY.LOAD_OPENINGS_FAILED');
      });
  }
}
